import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { TimerSuite } from "@/components/TimerSuite";
import { ExerciseVideoDialog } from "@/components/ExerciseVideoDialog";
import { ShareDialog } from "@/components/ShareDialog";
import { weightUnit } from "@/lib/units";
import { toast } from "sonner";
import { Clock, Flame, Timer as TimerIcon, CheckCircle2, ChevronLeft, PlayCircle, Lock, Dumbbell, Repeat } from "lucide-react";

const setCount = (ex) => {
  const n = parseInt(ex?.sets, 10);
  return Number.isFinite(n) && n > 0 && n <= 12 ? n : 3;
};

export default function WorkoutDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [w, setW] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rpe, setRpe] = useState([7]);
  const [notes, setNotes] = useState("");
  const [completeOpen, setCompleteOpen] = useState(false);
  const [videoName, setVideoName] = useState(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [weightLogs, setWeightLogs] = useState({}); // { "bi-ei": { name, sets:[{weight,reps}] } }
  const [swaps, setSwaps] = useState({});     // { "bi-ei": swappedName }
  const [altMap, setAltMap] = useState({});    // lowercase name -> [alternative names]
  const [openSwap, setOpenSwap] = useState(null); // "bi-ei" currently expanded

  // Trial users can SEE the weight-logging UI but it's locked until they're on a paid plan.
  const isTrial = user?.subscription_status === "trialing" && user?.role !== "admin";
  const unit = weightUnit(user);

  useEffect(() => {
    api.get(`/workouts/${id}`).then(({ data }) => setW(data)).finally(() => setLoading(false));
    api.get("/exercises").then(({ data }) => {
      const m = {};
      (data || []).forEach((e) => { m[(e.name || "").toLowerCase()] = e.alternatives || []; });
      setAltMap(m);
    }).catch(() => {});
  }, [id]);

  const openVideo = (name) => { setVideoName(name); setVideoOpen(true); };

  const setWeightVal = (key, name, idx, field, value) => {
    setWeightLogs((prev) => {
      const entry = prev[key] || { name, sets: [] };
      const sets = [...entry.sets];
      while (sets.length <= idx) sets.push({ weight: "", reps: "" });
      sets[idx] = { ...sets[idx], [field]: value };
      return { ...prev, [key]: { name, sets } };
    });
  };

  const buildExerciseLogs = () => {
    const out = [];
    Object.values(weightLogs).forEach((e) => {
      const sets = (e.sets || []).filter((s) => (s.weight !== "" && s.weight != null) || (s.reps !== "" && s.reps != null));
      if (sets.length) out.push({ name: e.name, unit, sets });
    });
    return out;
  };

  const markComplete = async () => {
    try {
      await api.post("/progress/workout-complete", {
        workout_id: w.id, workout_title: w.title, rpe: rpe[0], notes,
        exercise_logs: isTrial ? [] : buildExerciseLogs(),
      });
      toast.success("Workout logged. Well done.");
      setCompleteOpen(false);
      setShareOpen(true);
    } catch (e) {
      toast.error("Could not log workout");
    }
  };

  if (loading) return <AppShell title="Workout"><Skeleton className="h-64 w-full rounded-2xl bg-card" /></AppShell>;
  if (!w) return <AppShell title="Workout"><p className="text-muted-foreground">Workout not found.</p></AppShell>;

  return (
    <AppShell
      right={
        <Dialog>
          <DialogTrigger asChild>
            <Button data-testid="open-timer" size="sm" className="bg-secondary border border-border text-foreground hover:border-[#ccff0c]/40">
              <TimerIcon className="h-4 w-4 mr-1" /> Timers
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-popover border-border max-w-[440px]">
            <DialogHeader><DialogTitle className="font-display">Workout Timers</DialogTitle></DialogHeader>
            <TimerSuite />
          </DialogContent>
        </Dialog>
      }
    >
      <button onClick={() => navigate(-1)} className="flex items-center text-muted-foreground text-sm mb-3"><ChevronLeft className="h-4 w-4" /> Back</button>
      <div className="rd-fade-in space-y-5">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-secondary border border-border text-muted-foreground">{w.category}</Badge>
            <Badge className="bg-[#ccff0c]/15 text-[#ccff0c] border border-[#ccff0c]/20">{w.difficulty}</Badge>
          </div>
          <h1 className="font-display text-3xl font-bold">{w.title}</h1>
          <p className="text-muted-foreground mt-2">{w.description}</p>
          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-3">
            <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {w.duration_min} min</span>
            <span className="flex items-center gap-1"><Flame className="h-4 w-4" /> {(w.equipment || []).join(", ") || "Bodyweight"}</span>
          </div>
        </div>

        {w.coaching_notes && (
          <Card className="rounded-2xl bg-[#ccff0c]/10 border-[#ccff0c]/20 p-4">
            <p className="text-xs uppercase tracking-wide text-[#ccff0c] font-semibold mb-1">Coaching notes</p>
            <p className="text-sm text-muted-foreground">{w.coaching_notes}</p>
          </Card>
        )}

        {(w.blocks || []).map((block, bi) => (
          <div key={bi}>
            <h3 className="font-display text-lg font-bold mb-2">{block.name}</h3>
            <div className="space-y-2">
              {(block.exercises || []).map((ex, ei) => {
                const key = `${bi}-${ei}`;
                const displayName = swaps[key] || ex.name;
                const alts = (altMap[(ex.name || "").toLowerCase()] || []).filter((a) => a !== displayName);
                return (
                <Card
                  key={ei}
                  data-testid={`workout-exercise-${bi}-${ei}`}
                  className="rounded-xl bg-card border-border p-3 hover:border-[#ccff0c]/40 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => openVideo(displayName)}>
                      <PlayCircle className="h-5 w-5 text-[#ccff0c] flex-shrink-0" />
                      <div>
                        <p className="font-semibold">{displayName}{swaps[key] && <span className="text-[10px] text-[#ccff0c] ml-1">(swapped)</span>}</p>
                        {ex.notes && <p className="text-xs text-muted-foreground mt-0.5">{ex.notes}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right cursor-pointer" onClick={() => openVideo(displayName)}>
                        <p className="font-mono text-[#ccff0c] font-semibold">{ex.sets}×{ex.reps}</p>
                        <p className="text-[10px] text-muted-foreground">watch demo</p>
                      </div>
                      {alts.length > 0 && (
                        <button
                          data-testid={`swap-btn-${bi}-${ei}`}
                          onClick={(e) => { e.stopPropagation(); setOpenSwap(openSwap === key ? null : key); }}
                          title="Swap exercise"
                          className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-[#ccff0c] hover:border-[#ccff0c]/40"
                        >
                          <Repeat className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  {openSwap === key && alts.length > 0 && (
                    <div data-testid={`swap-list-${bi}-${ei}`} className="mt-2 pt-2 border-t border-border">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">Swap for a similar movement</p>
                      <div className="flex flex-wrap gap-1.5">
                        {swaps[key] && (
                          <button onClick={() => { setSwaps((s) => { const n = { ...s }; delete n[key]; return n; }); setOpenSwap(null); }}
                            className="rounded-full px-2.5 py-1 text-xs border border-border bg-secondary text-muted-foreground">Reset to {ex.name}</button>
                        )}
                        {alts.map((a) => (
                          <button key={a} data-testid={`swap-opt-${bi}-${ei}-${a.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                            onClick={() => { setSwaps((s) => ({ ...s, [key]: a })); setOpenSwap(null); }}
                            className="rounded-full px-2.5 py-1 text-xs border border-[#ccff0c]/30 bg-[#ccff0c]/10 text-[#ccff0c] hover:bg-[#ccff0c]/20">{a}</button>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              );})}
            </div>
          </div>
        ))}

        <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
          <DialogTrigger asChild>
            <Button data-testid="complete-workout" className="w-full h-12 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] rd-glow font-semibold">
              <CheckCircle2 className="h-5 w-5 mr-2" /> Complete Workout
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-popover border-border max-w-[420px] max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display">Log this session</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">How hard was it? (RPE): <span className="text-[#ccff0c] font-mono">{rpe[0]}</span></p>
                <Slider data-testid="rpe-slider" value={rpe} onValueChange={setRpe} min={1} max={10} step={1} />
              </div>
              <Textarea data-testid="workout-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" className="bg-card border-border" />

              {/* Optional per-set weight logging (paywalled for trial users) */}
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold flex items-center gap-1.5"><Dumbbell className="h-4 w-4 text-[#ccff0c]" /> Log weights used <span className="text-muted-foreground font-normal">(optional)</span></p>
                  <span className="text-[11px] text-muted-foreground">{unit}</span>
                </div>
                <div className={`space-y-3 max-h-[34vh] overflow-y-auto rd-scrollbar pr-1 ${isTrial ? "blur-sm pointer-events-none select-none" : ""}`} data-testid="weight-log-section">
                  {(w.blocks || []).flatMap((block, bi) =>
                    (block.exercises || []).map((ex, ei) => {
                      const key = `${bi}-${ei}`;
                      const n = setCount(ex);
                      return (
                        <div key={key} className="rounded-xl bg-muted border border-border p-3">
                          <p className="text-sm font-medium mb-2">{ex.name}</p>
                          <div className="space-y-1.5">
                            {Array.from({ length: n }).map((_, si) => (
                              <div key={si} className="flex items-center gap-2">
                                <span className="text-[11px] text-muted-foreground w-10">Set {si + 1}</span>
                                <Input data-testid={`weight-${key}-${si}`} type="number" inputMode="decimal" placeholder={unit}
                                  value={weightLogs[key]?.sets?.[si]?.weight ?? ""}
                                  onChange={(e) => setWeightVal(key, ex.name, si, "weight", e.target.value)}
                                  className="bg-card border-border h-9 text-sm" />
                                <span className="text-muted-foreground text-xs">×</span>
                                <Input data-testid={`reps-${key}-${si}`} type="number" inputMode="numeric" placeholder="reps"
                                  value={weightLogs[key]?.sets?.[si]?.reps ?? ""}
                                  onChange={(e) => setWeightVal(key, ex.name, si, "reps", e.target.value)}
                                  className="bg-card border-border h-9 text-sm" />
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                {isTrial && (
                  <div data-testid="weight-log-lock" className="absolute inset-0 top-7 flex flex-col items-center justify-center rounded-xl bg-black/70 backdrop-blur-sm text-center px-4">
                    <div className="h-11 w-11 rounded-2xl bg-[#ccff0c]/15 flex items-center justify-center mb-2"><Lock className="h-5 w-5 text-[#ccff0c]" /></div>
                    <p className="font-semibold text-sm">Weight logging is a paid feature</p>
                    <p className="text-xs text-muted-foreground mt-1 mb-3 max-w-[240px]">Track the weights you lift for every set once you're on Standard.</p>
                    <Button data-testid="weight-log-unlock" onClick={() => { setCompleteOpen(false); navigate("/subscribe"); }} className="h-9 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold text-sm">
                      Unlock with Standard
                    </Button>
                  </div>
                )}
              </div>

              <Button data-testid="confirm-complete" onClick={markComplete} className="w-full bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <ExerciseVideoDialog name={videoName} open={videoOpen} onOpenChange={setVideoOpen} />
      <ShareDialog
        open={shareOpen}
        onOpenChange={(o) => { setShareOpen(o); if (!o) navigate("/progress"); }}
        defaultText={w ? `Crushed "${w.title}" on Real Deal Fitness 💪 ${w.duration_min} min · ${w.category} · ${w.difficulty}` : ""}
        title="Share your workout"
      />
    </AppShell>
  );
}
